import { useCallback, useEffect, useState } from 'react';

import type { ISearchParameters } from '@features/filters/types';
import type { MODULE } from '@modules/types';
import type { IColumnsConfig } from '@shared/table/types';
import type { OnChangeFn, SortingState, Updater } from '@tanstack/react-table';

import type { Action as ReduxAction } from '@reduxjs/toolkit';

import { setColumnsConfig } from '@features/customizeTable/store/customizeTableSlice';
import { convertSortingPayloadToTableValue } from '@features/filters/utils/convertSortingPayloadToTableValue';
import { convertSortingToSearchPayload } from '@features/filters/utils/convertSortingToSearchPayload';

import { useAppDispatch } from '@hooks';

type Action = (data: ISearchParameters) => ReduxAction;

interface IUseSetSearchParametersResult {
  sorting: SortingState;
  searchParameters: ISearchParameters;
  setPageSize: (size: number) => void;
  setPageIndex: (page: number) => void;
  setSorting: OnChangeFn<SortingState>;
}

const resolveSortingUpdater = (
  updater: Updater<SortingState>,
  previousSorting: SortingState
): SortingState =>
  typeof updater === 'function' ? updater(previousSorting) : updater;

export const useSetSearchParameters = ({
  action,
  module,
  columnsConfig,
  searchParameters,
}: {
  action: Action;
  module?: MODULE;
  searchParameters: ISearchParameters;
  columnsConfig?: null | IColumnsConfig;
}): IUseSetSearchParametersResult => {
  const dispatch = useAppDispatch();

  const [sortingTable, setSortingTable] = useState<SortingState>([]);

  useEffect(() => {
    const defaultSortingState = convertSortingPayloadToTableValue(
      searchParameters.sorts
    );

    setSortingTable(defaultSortingState);
  }, [searchParameters.sorts, columnsConfig]);

  const setPageIndex = useCallback(
    (page: number): void => {
      dispatch(action({ ...searchParameters, page }));
    },
    [action, dispatch, searchParameters]
  );

  const setPageSize = useCallback(
    (size: number): void => {
      dispatch(action({ ...searchParameters, size, page: 1 }));
      if (module && columnsConfig) {
        dispatch(
          setColumnsConfig({
            [module]: { ...columnsConfig, itemPerPage: size },
          })
        );
      }
    },
    [action, columnsConfig, dispatch, module, searchParameters]
  );

  const setSorting = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const internalSortingTableData = resolveSortingUpdater(
        updater,
        sortingTable
      );
      const sortingPayload = convertSortingToSearchPayload(
        internalSortingTableData
      );

      setSortingTable(internalSortingTableData);
      dispatch(action({ ...searchParameters, sorts: sortingPayload }));

      if (module && columnsConfig?.sortBy && columnsConfig?.orderDirection) {
        dispatch(
          setColumnsConfig({
            [module]: {
              ...columnsConfig,
              sortBy: sortingPayload[0].field,
              orderDirection: sortingPayload[0].order,
            },
          })
        );
      }
    },
    [action, columnsConfig, dispatch, module, searchParameters, sortingTable]
  );

  return {
    setSorting,
    setPageSize,
    setPageIndex,
    searchParameters,
    sorting: sortingTable,
  };
};
